import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiExternalLink, FiHeart, FiShare2, FiShield, FiTruck } from "react-icons/fi";
import styles from "../../styles/pages/Product/product-details.module.css";
import { Product } from "../../interfaces/product/types/producttype";
import { getAmazonProductInfo, getProductBySlug, getProducts, AmazonProductInfo } from "../../data-access/apis/products/productApi";
import { createProductReview, getProductReviews, ProductReview } from "../../data-access/apis/reviews/reviewApi";
import { trackAmazonClick } from "../../utils/analytics/trackAmazonClick";
import { GetTokenFromIndexedDB } from "../../utils/indexedDBUtils/get-indexed-db";
import SEO from "../../utils/seo/SEO";
import { getCategoryLabel } from "../../data/productCategories";

const ProductDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [amazonInfo, setAmazonInfo] = useState<AmazonProductInfo | null>(null);
  const [related, setRelated] = useState<Product[]>([]);
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [reviewSummary, setReviewSummary] = useState({ average: 0, count: 0 });
  const [activeImage, setActiveImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [amazonLoading, setAmazonLoading] = useState(false);
  const [error, setError] = useState(false);
  const [liked, setLiked] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewMessage, setReviewMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!slug) { setError(true); setLoading(false); return; }
      setLoading(true);
      setError(false);
      try {
        const found = await getProductBySlug(slug);
        if (cancelled) return;
        setProduct(found);
        setActiveImage(0);

        const [relatedProducts, reviewData, token] = await Promise.all([
          getProducts({ category: found.category, audience: found.audience }),
          getProductReviews(slug),
          GetTokenFromIndexedDB("authToken"),
        ]);
        if (cancelled) return;
        setRelated(relatedProducts.filter((item) => item._id !== found._id).slice(0, 4));
        setReviews(reviewData.reviews);
        setReviewSummary(reviewData.summary);
        setSignedIn(!!token);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug]);

  // PA-API is deliberately called only after this product detail route is open.
  useEffect(() => {
    let cancelled = false;
    if (!slug || !product?.asin) return;
    setAmazonLoading(true);
    getAmazonProductInfo(product.asin)
      .then((data) => { if (!cancelled) setAmazonInfo(data); })
      .catch(() => { if (!cancelled) setAmazonInfo(null); })
      .finally(() => { if (!cancelled) setAmazonLoading(false); });
    return () => { cancelled = true; };
  }, [slug, product?.asin]);

  const amazonImages = useMemo(() => Array.from(new Set((amazonInfo?.images || []).filter(Boolean))), [amazonInfo]);
  const siteImages = useMemo(() => Array.from(new Set((product?.images || []).filter(Boolean))), [product]);
  const images = useMemo(() => Array.from(new Set([...siteImages, ...amazonImages])), [siteImages, amazonImages]);

  const currentImage = images[activeImage] || images[0];
  const productDescription = product?.description?.trim() || `Explore ${product?.title || "this product"} on MyOnlineShopping and check Amazon for current marketplace information.`;
  const amazonRating = amazonInfo?.amazonRating || 0;
  const amazonRatingCount = amazonInfo?.amazonRatingCount || 0;

  const handleShare = async () => {
    try {
      if (navigator.share) await navigator.share({ title: product?.title || "Product", text: product?.title || "Check this product", url: window.location.href });
      else await navigator.clipboard.writeText(window.location.href);
    } catch { /* cancelled */ }
  };

  const scrollToReviews = () => {
    document.getElementById("product-reviews")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitReview = async (event: FormEvent) => {
    event.preventDefault();
    if (!slug || !signedIn) return;
    setReviewSaving(true);
    setReviewMessage("");
    try {
      await createProductReview(slug, { rating: reviewRating, comment: reviewComment.trim() });
      const fresh = await getProductReviews(slug);
      setReviews(fresh.reviews);
      setReviewSummary(fresh.summary);
      setReviewComment("");
      setReviewMessage("Your feedback was added successfully.");
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        setSignedIn(false);
        setReviewMessage("Please sign in again to submit feedback.");
      } else {
        setReviewMessage(error?.response?.data?.message || "Unable to submit feedback.");
      }
    } finally {
      setReviewSaving(false);
    }
  };

  if (loading) {
    return <main className={styles.page}><div className={styles.loadingShell}><div className={styles.loadingImage} /><div className={styles.loadingContent}><span /><span /><span /><span /></div></div></main>;
  }

  if (error || !product) {
    return <main className={styles.page}><section className={styles.notFound}><div className={styles.notFoundIcon}>⌕</div><p className={styles.kicker}>PRODUCT NOT FOUND</p><h1>This product is no longer available.</h1><p>It may have been removed or is temporarily unavailable. You can continue exploring the catalogue.</p><button type="button" onClick={() => navigate("/products")}>Back to shopping</button></section></main>;
  }

  return (
    <>
      <SEO title={`${product.title} | MyOnlineShopping`} description={productDescription.slice(0, 160)} canonicalPath={`/product/${encodeURIComponent(product.slug)}`} image={images[0]} />
      <main className={styles.page}>
        <div className={styles.container}>
          <button type="button" className={styles.backButton} onClick={() => navigate(-1)}><FiArrowLeft /> Back</button>
          <div className={styles.breadcrumb}><button type="button" onClick={() => navigate("/")}>Home</button><span>/</span><button type="button" onClick={() => navigate(`/category/${product.category}`)}>{getCategoryLabel(product.category)}</button><span>/</span><strong>{product.title}</strong></div>

          <section className={styles.productShell}>
            <div className={styles.gallery}>
              <div className={styles.badges}>{product.isTrending && <span>🔥 Trending</span>}{product.isFeatured && <span>★ Featured</span>}{product.isDiwaliDeal && <span>🎆 Diwali</span>}</div>
              <div className={styles.mainImage}>
                {currentImage ? (
                  <img
                    src={currentImage}
                    alt={product.title}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                      event.currentTarget.parentElement?.classList.add(styles.imageError);
                    }}
                  />
                ) : (
                  <div className={styles.imageFallback}>Product image unavailable</div>
                )}
              </div>
              {images.length > 1 && (
                <div className={styles.thumbnails} aria-label="Product images">
                  {images.map((image, index) => (
                    <button
                      type="button"
                      key={`${image}-${index}`}
                      className={index === activeImage ? styles.activeThumb : ""}
                      onClick={() => setActiveImage(index)}
                      aria-label={`View image ${index + 1}`}
                      aria-pressed={index === activeImage}
                    >
                      <img
                        src={image}
                        alt=""
                        loading="lazy"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
              {amazonLoading && <div className={styles.amazonSyncNote}>Loading latest Amazon product information…</div>}
            </div>

            <div className={styles.info}>
              <p className={styles.category}>{getCategoryLabel(product.category)}</p>
              <h1>{product.title}</h1>
              <div className={styles.metaRow}><span>{product.subCategory || "Product"}</span><span>•</span><span>ASIN {product.asin || "Not available"}</span></div>

              <button type="button" className={styles.viewReviewsButton} onClick={scrollToReviews}>View comments & feedback</button>

              <div className={styles.ratingPanel}>
                <div><strong>{amazonRating ? `★ ${amazonRating.toFixed(1)}` : "Amazon rating"}</strong><span>{amazonRatingCount ? `${amazonRatingCount.toLocaleString("en-IN")} ratings` : "Loaded from Amazon when available"}</span></div>
                <div><strong>{reviewSummary.count ? `★ ${reviewSummary.average.toFixed(1)}` : "No feedback yet"}</strong><span>{reviewSummary.count} MyOnlineShopping feedback{reviewSummary.count === 1 ? "" : "s"}</span></div>
              </div>

              <p className={styles.priceNote}>Current price and marketplace availability are intentionally not shown here. Use the Amazon button below to see the current information on Amazon.</p>

              <div className={styles.actions}>
                <a className={styles.primaryAction} href={product.amazonUrl} target="_blank" rel="sponsored noopener noreferrer" onClick={() => trackAmazonClick(product, "product-details")}>View Price on Amazon <FiExternalLink /></a>
                <button type="button" className={styles.iconAction} onClick={() => setLiked((v) => !v)} aria-label="Save product"><FiHeart className={liked ? styles.liked : ""} /></button>
                <button type="button" className={styles.iconAction} onClick={handleShare} aria-label="Share product"><FiShare2 /></button>
              </div>
              <p className={styles.affiliateDisclosure}><span>Paid link</span> · As an Amazon Associate I earn from qualifying purchases.</p>

              <div className={styles.trustRow}><div><FiShield /><span><strong>Marketplace checkout</strong><small>Purchase continues on Amazon</small></span></div><div><FiTruck /><span><strong>Delivery information</strong><small>Check the latest details on Amazon</small></span></div></div>

              <div className={styles.description}><h2>About this product</h2><p>{productDescription}</p></div>
              {(amazonInfo?.brand || amazonInfo?.manufacturer) && <div className={styles.specGrid}>{amazonInfo.brand && <div><span>Brand</span><strong>{amazonInfo.brand}</strong></div>}{amazonInfo.manufacturer && <div><span>Manufacturer</span><strong>{amazonInfo.manufacturer}</strong></div>}</div>}
              {!!amazonInfo?.features?.length && <div className={styles.features}><h2>Key product features</h2><ul>{amazonInfo.features.slice(0, 8).map((feature, index) => <li key={`${feature}-${index}`}>{feature}</li>)}</ul></div>}
              {amazonInfo && <p className={styles.amazonContentDisclaimer}>CERTAIN CONTENT THAT APPEARS ON THIS SITE COMES FROM AMAZON SELLER SERVICES PRIVATE LIMITED. THIS CONTENT IS PROVIDED 'AS IS' AND IS SUBJECT TO CHANGE OR REMOVAL AT ANY TIME.</p>}
            </div>
          </section>

          <section id="product-reviews" className={styles.reviewSection}>
            <div className={styles.sectionHead}><div><p className={styles.kicker}>COMMUNITY FEEDBACK</p><h2>What shoppers are saying</h2></div><div className={styles.reviewSummary}><strong>{reviewSummary.average ? reviewSummary.average.toFixed(1) : "—"}</strong><span>★ · {reviewSummary.count} feedback</span></div></div>

            <div className={styles.reviewLayout}>
              <div className={styles.reviewList}>
                {reviews.length ? reviews.map((review) => (
                  <article className={styles.reviewCard} key={review._id}>
                    <div className={styles.reviewTop}><strong>{review.userName}</strong><span>{"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}</span></div>
                    <p>{review.comment}</p>
                    <small>{new Date(review.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</small>
                  </article>
                )) : <div className={styles.emptyReviews}><strong>No feedback yet</strong><span>Be the first shopper to share a useful opinion.</span></div>}
              </div>

              <form className={styles.reviewForm} onSubmit={submitReview}>
                <h3>Share your feedback</h3>
                {signedIn ? <>
                  <div className={styles.starPicker} aria-label="Choose rating">{[1, 2, 3, 4, 5].map((value) => <button type="button" key={value} className={value <= reviewRating ? styles.starActive : ""} onClick={() => setReviewRating(value)} aria-label={`${value} stars`}>★</button>)}</div>
                  <textarea required minLength={3} maxLength={1000} value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Tell other shoppers what is useful about this product…" rows={5} />
                  <button type="submit" disabled={reviewSaving}>{reviewSaving ? "Submitting…" : "Submit feedback"}</button>
                </> : <>
                  <p>Sign in to leave your own rating and comment.</p>
                  <button type="button" onClick={() => navigate("/auth/sign-in")}>Sign in to review</button>
                </>}
                {reviewMessage && <div className={styles.reviewMessage}>{reviewMessage}</div>}
              </form>
            </div>
          </section>

          <section className={styles.related}>
            <div className={styles.sectionHead}>
              <div>
                <p className={styles.kicker}>KEEP EXPLORING</p>
                <h2>More from {getCategoryLabel(product.category)}</h2>
              </div>
              <button type="button" onClick={() => navigate(`/category/${product.category}`)}>Explore category</button>
            </div>

            {related.length ? (
              <div className={styles.relatedGrid}>
                {related.map((item) => (
                  <button type="button" className={styles.relatedCard} key={item._id} onClick={() => navigate(`/product/${item.slug}`)}>
                    <div className={styles.relatedImage}>
                      {item.images?.[0] ? (
                        <img
                          src={item.images[0]}
                          alt={item.title}
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                            event.currentTarget.parentElement?.classList.add(styles.imageError);
                          }}
                        />
                      ) : (
                        <span>Product image unavailable</span>
                      )}
                    </div>
                    <div className={styles.relatedBody}>
                      <span>{getCategoryLabel(item.category)}</span>
                      <strong>{item.title}</strong>
                      <div>View details</div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className={styles.relatedEmpty}>
                <strong>More picks are on the way</strong>
                <span>There are no other active products in this category yet.</span>
                <button type="button" onClick={() => navigate("/products")}>Browse all products</button>
              </div>
            )}
          </section>
        </div>
      </main>
    </>
  );
};

export default ProductDetails;
